import { Box, Stack, Switch, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { SchemeKnobValues, SchemeManifest } from "@shared/types/foyer";
import type { TemplateSubSection } from "../PremiseSidebar";
import { INFO_ZONE, screenState } from "./screensModel";

interface Props {
  sub: TemplateSubSection;
  manifest: SchemeManifest | undefined;
  value: SchemeKnobValues | undefined;
  onChange: (next: SchemeKnobValues) => void;
}

/**
 * The on/off switch of the section you are editing, shown at the top of its
 * own editor.
 *
 * Before this, on/off lived only under Toggles: you could fill in a section
 * with no hint that it was switched off, and switching one off deleted the tab
 * you were standing on. Now the switch travels with the content.
 *
 * One switch named after the section ("Promotions on"), on or off alike
 * (Sigge, 2026-10-30). It used to say "This screen is off" with a Turn on
 * button, but a screen is a TV here, not a part of the template.
 */
export default function ScreenStateBar({
  sub,
  manifest,
  value,
  onChange,
}: Props) {
  const { t } = useTranslation("templates");
  const state = screenState(sub, manifest, value);
  if (!state) return null;
  const name = t(`templateSubMessages.subNoun.${sub}`, {
    defaultValue: state.label,
  });

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      data-testid="screen-state-bar"
      sx={{ px: 0.5 }}
    >
      <Switch
        size="small"
        checked={state.on}
        onChange={(e) =>
          onChange({ ...(value ?? {}), [state.knobId]: e.target.checked })
        }
        inputProps={
          {
            "aria-label": t("screenStateBar.show", { name }),
            "data-testid": "screen-state-switch",
          } as React.InputHTMLAttributes<HTMLInputElement>
        }
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: "fontWeightMedium" }}>
          {state.on
            ? t("screenStateBar.on", { name })
            : t("screenStateBar.off", { name })}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {!state.on
            ? t("screenStateBar.offHint")
            : state.placement === INFO_ZONE
              ? t("screenStateBar.onInsideInformation")
              : t("screenStateBar.onHomeScreen")}
        </Typography>
      </Box>
    </Stack>
  );
}
